import type { Shape } from '../../shared/types.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor } from '../editor.ts';
import { rectFromPoints } from '../util/geometry.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const CLICK_PX = 4;

/** Drag to draw a shape of the editor's current kind (shift = equal sides); click for a default size. */
export class ShapeTool implements Tool {
  cursor = 'crosshair';
  private readonly ed: Editor;
  private drag: { id: string; x: number; y: number; sx: number; sy: number } | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    const ed = this.ed;
    const obj: Shape = {
      id: newId(), type: 'shape', kind: ed.shapeKind,
      x: e.x, y: e.y, w: 1, h: 1, rotation: 0,
      z: ed.zKeysOnTop(1)[0]!, createdBy: ed.userId,
      fill: '#FFFFFF', stroke: '#1A1A1A', strokeWidth: 2,
    };
    ed.beginGesture();
    ed.apply([{ t: 'create', obj }]);
    ed.renderer.setActive([obj.id]);
    this.drag = { id: obj.id, x: e.x, y: e.y, sx: e.sx, sy: e.sy };
  }

  onPointerMove(e: WorldPointerEvent): void {
    const d = this.drag;
    if (!d) return;
    let w = e.x - d.x, h = e.y - d.y;
    if (e.shiftKey) {
      const s = Math.max(Math.abs(w), Math.abs(h));
      w = Math.sign(w || 1) * s;
      h = Math.sign(h || 1) * s;
    }
    const r = rectFromPoints(d.x, d.y, d.x + w, d.y + h);
    this.ed.apply([{ t: 'update', id: d.id, props: { x: r.x, y: r.y, w: Math.max(r.w, 1), h: Math.max(r.h, 1) } }]);
  }

  onPointerUp(e: WorldPointerEvent): void {
    const d = this.drag;
    if (!d) return;
    const ed = this.ed;
    if (Math.hypot(e.sx - d.sx, e.sy - d.sy) < CLICK_PX) {
      const [w, h] = ed.shapeKind === 'rect' ? [160, 100] : [120, 120];
      ed.apply([{ t: 'update', id: d.id, props: { x: d.x - w / 2, y: d.y - h / 2, w, h } }]);
    }
    ed.endGesture();
    ed.renderer.setActive([]);
    this.drag = null;
    ed.setTool('select');
    ed.setSelection([d.id]);
  }

  onCancel(): void {
    if (!this.drag) return;
    this.ed.renderer.setActive([]);
    this.ed.cancelGesture();
    this.drag = null;
  }
}
